<template>
  <SparkModal dismissable :model-value="show" @update:model-value="close">
    <template #header>
      <div class="flex flex-col">
        <div class="text-15 font-medium">Save Calibration Results</div>
        <div class="text-11 text-gray-400 font-normal">Save the calibrated machine and material parameters</div>
      </div>
    </template>

    <template #default>
      <div class="flex flex-col gap-16">
        <!-- Calibration Name -->
        <SparkInput v-model="calibrationName" label="Calibration Name" name="name" />

        <!-- Machine/Material Mode -->
        <div v-if="selectedMode === 'machine'" class="flex flex-col gap-16">
          <!-- Save Mode Selection - Only show if previously calibrated -->
          <div v-if="hasExistingCalibration" class="flex flex-col gap-8">
            <div class="text-14 font-medium">Save Options</div>
            <div class="flex flex-col gap-4">
              <div>
              <SparkRadio
                v-model="saveMode"
                name="save-mode"
                value="create"
                label="Create new calibrated material and machine"
                />
              <label class="text-14">Create new calibrated material and machine</label>
              </div>
              <div>
                <SparkRadio
                  v-model="saveMode"
                  name="save-mode"
                  value="update"
                  label="Update existing calibrated material and machine"
                />
                <label class="text-14">Update existing calibrated material and machine</label>
              </div>
            </div>
          </div>

          <!-- Input fields for new resources -->
          <div v-if="saveMode === 'create'" class="flex flex-col gap-8">
            <SparkInput 
              v-model="materialName" 
              label="Material Name" 
              name="materialname"
              :placeholder="'Calibration - ' + calibrationResult?.material_name"
            />
            <SparkInput 
              v-model="machineName" 
              label="Machine Name" 
              name="machinename"
              :placeholder="'Calibration - ' + calibrationResult?.machine_name"
            />
          </div>

          <!-- Display existing resources when updating -->
          <div v-else-if="saveMode === 'update'" class="flex flex-col gap-8">
            <div class="flex flex-col gap-4 p-8 bg-gray-50 rounded-8">
              <div class="flex justify-between">
                <span class="text-gray-500">Material:</span>
                <span>{{ calibrationResult?.calibrated_material_name }}</span>
              </div>
              <div class="flex justify-between">
                <span class="text-gray-500">Machine:</span>
                <span>{{ calibrationResult?.calibrated_machine_name }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Pricing Mode -->
        <div v-if="selectedMode === 'price'" class="flex flex-col gap-8">
          <SparkInput v-model="pricingName" label="Pricing Name" name="pricingname" />
        </div>

        <!-- Template Creation -->
        <div v-if="selectedMode === 'machine'" class="flex flex-col gap-8">
          <div class="flex items-center gap-8">
            <SparkCheckbox v-model="createTemplate" name="createTemplate" />
            <label class="text-14">Create Process Chain Template</label>
          </div>
          <SparkInput 
            v-if="createTemplate" 
            v-model="templateName" 
            label="Template Name" 
            name="templateName"
            :placeholder="'Template - ' + calibrationResult?.machine_name + ' - ' + calibrationResult?.material_name"
          />
        </div>

        <!-- Action Buttons -->
        <div class="flex gap-8 justify-end mt-8">
          <SparkButton variant="outlined" @click="close()">Cancel</SparkButton>
          <SparkButton variant="secondary" @click="save()">Save</SparkButton>
        </div>
      </div>
    </template>
  </SparkModal>
</template>

<script>
import { mapState } from 'vuex';

import SparkButton from '@/components/SparkComponents/SparkButton.vue';
import SparkCheckbox from '@/components/SparkComponents/SparkCheckbox.vue';
import SparkInput from '@/components/SparkComponents/SparkInput.vue';
import SparkModal from '@/components/SparkComponents/SparkModal.vue';
import SparkRadio from '@/components/SparkComponents/SparkRadio.vue';

export default {
  name: 'SaveResultsModal',

  components: {
    SparkModal,
    SparkInput,
    SparkButton,
    SparkCheckbox,
    SparkRadio,
  },

  props: {
    show: { type: Boolean },
  },

  emits: ['close'],

  data() {
    return {
      calibrationName: '',
      materialName: '',
      machineName: '',
      pricingName: '',
      createTemplate: false,
      templateName: '',
      saveMode: 'create',
      resultingPricingConfigId: null,
    };
  },

  computed: {
    ...mapState('application', ['axiosInstance']),
    ...mapState(['user']),
    ...mapState('calibration', [
      'selectedMode',
      'calibrationConfigUid',
      'calibrationResult',
    ]),

    fromSupplier() {
      return localStorage.getItem('pricingOrigin') === 'supplier';
    },

    hasExistingCalibration() {
      return (
        this.calibrationResult?.calibrated_machine_uid &&
        this.calibrationResult?.calibrated_material_uid
      );
    },
  },

  watch: {
    show(newVal) {
      if (newVal) {
        this.initializeFormData();
      }
    },
  },

  methods: {
    initializeFormData() {
      this.calibrationName = this.calibrationResult?.name || '';
      this.materialName = this.calibrationResult?.material_name ? 
        `Calibration - ${this.calibrationResult.material_name}` : '';
      this.machineName = this.calibrationResult?.machine_name ? 
        `Calibration - ${this.calibrationResult.machine_name}` : '';
      this.templateName = this.calibrationResult?.machine_name && this.calibrationResult?.material_name ? 
        `Template - ${this.calibrationResult.machine_name} - ${this.calibrationResult.material_name}` : '';
      
      if (this.selectedMode === 'price') {
        const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
        const username = this.user?.username || 'user';
        this.pricingName = this.pricingName || `Target Pricing ${now} ${username}`;
      }
    },

    close() {
      this.$emit('close');
    },

    async save() {
      if (!this.calibrationName || this.calibrationName.trim() === '') {
        this.$root.notify('error', 'Validation Error', 'Please provide a calibration name before saving.', 5000);
        return;
      }

      if (this.selectedMode === 'price' && (!this.pricingName || this.pricingName.trim() === '')) {
        this.$root.notify('error', 'Validation Error', 'Please provide a pricing name before saving.', 5000);
        return;
      }

      if (this.selectedMode === 'machine' && this.saveMode === 'create') {
        if (!this.machineName || this.machineName.trim() === '') {
          this.$root.notify('error', 'Validation Error', 'Please provide a machine name before saving.', 5000);
          return;
        }
        if (!this.materialName || this.materialName.trim() === '') {
          this.$root.notify('error', 'Validation Error', 'Please provide a material name before saving.', 5000);
          return;
        }
      }

      // First update the calibration config name
      await this.updateCalibrationConfig();
      // Then create or update the calibrated results
      await this.createCalibratedResult();
      
      // If coming from supplier, redirect back to supplier creation
      if (this.fromSupplier) {
        this.finishAndRedirect();
      }
    },

    async updateCalibrationConfig() {
      const data = {
        calibration_config_name: this.calibrationName,
      };

      try {
        await this.axiosInstance.put(
          `api/v1/calibration-config-list/${this.calibrationConfigUid}`, 
          data
        );
      } catch (error) {
        console.error(error);
        this.$root.notify('error', 'Error updating calibration config', error.message, 10000);
      }
    },

    async createCalibratedResult() {
      const data = {
        material_name: this.materialName,
        machine_name: this.machineName,
        pricing_name: this.pricingName,
        create_template: this.createTemplate,
        template_name: this.templateName,
        save_mode: this.hasExistingCalibration ? this.saveMode : 'create',
      };

      if (this.saveMode === 'update' && this.hasExistingCalibration) {
        data.calibrated_machine_uid = this.calibrationResult.calibrated_machine_uid;
        data.calibrated_material_uid = this.calibrationResult.calibrated_material_uid;
      }

      try {
        const response = await this.axiosInstance.post(
          `api/v1/calibration-result/${this.calibrationConfigUid}`, 
          data
        );

        if (response.data && response.data.pricing_config_uid) {
          this.resultingPricingConfigId = response.data.pricing_config_uid;
        }

        let title, text;
        if (this.selectedMode === 'price') {
          title = 'Pricing configuration saved';
          text = `Pricing configuration created: ${response.data.pricing_name}`;
        } else {
          const actionText = this.saveMode === 'update' ? 'updated' : 'created';
          title = `Machine and material ${actionText}`;
          text = `Machine/material combination ${actionText}: ${response.data.machine_name} / ${response.data.material_name}`;
        }
        
        if (this.createTemplate) {
          text += `\nTemplate created: ${response.data.template_name}`;
        }
        
        this.$root.notify('success', title, text, 10000);
        this.close();
        
        // Only redirect if not coming from supplier flow
        if (!this.fromSupplier) {
          this.$router.push({ path: '/library/part' });
        }
      } catch (error) {
        console.error(error);
        this.$root.notify('error', 'Error saving calibration', error.message, 10000);
      }
    },

    finishAndRedirect() {
      localStorage.removeItem('pricingOrigin');
      
      const supplierDataString = localStorage.getItem('pendingSupplierData');
      
      if (supplierDataString) {
        try {
          const supplierData = JSON.parse(supplierDataString);
          
          // Update the stored data with the new pricing configuration from calibration
          supplierData.currentCombination = supplierData.currentCombination || {};
          supplierData.currentCombination.newPricingConfig = {
            configId: this.resultingPricingConfigId,
            configName: this.selectedMode === 'price' ? this.pricingName : this.calibrationName
          };
          
          
          // Store the updated data
          localStorage.setItem('pendingSupplierData', JSON.stringify(supplierData));
          
          // Redirect back to supplier
          this.$router.push({
            path: '/settings/supplier-settings',
            query: { 
              returnFromPricing: 'true',
              openCreateModal: 'true'
            }
          });
        } catch (e) {
          console.error("Error updating supplier data with new pricing config from calibration:", e);
          this.close();
          this.$router.push({ path: '/library/part' });
        }
      } else {
        console.error("No supplier data found in localStorage");
        this.close();
        this.$router.push({ path: '/library/part' });
      }
    },
  },
};
</script>
